import React from 'react';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PassTargetDetail, PassTargetType } from '../../models/Pass';
import type { TutorLessonOption } from '../../models/Tutor';
import TutorLessonOptionCard from '../tutor/TutorLessonOptionCard';
import PassEligibleTutorsRow from './PassEligibleTutorsRow';
import PassSpaceTargetCard from './PassSpaceTargetCard';
import { normalizeTutorSessionType } from '../../utils/tutorSessionType';

export interface PassTargetCollectionProps {
  targetType: PassTargetType;
  targetIds: string[];
  targets: PassTargetDetail[];
  /** Host offer detail can deep-link room / lesson rows. */
  audience?: 'user' | 'host' | 'tutor';
  onRoomClick?: (target: PassTargetDetail) => void;
  onLessonClick?: (target: PassTargetDetail) => void;
}

const toLessonOptionCard = (target: PassTargetDetail): TutorLessonOption => ({
  id: target.id,
  name: target.name?.trim() || target.id,
  description: target.description,
  durationMinutes: target.durationMinutes ?? 0,
  price: target.price ?? 0,
  lessonPriceCurrency: (target.currencyCode as 'PLN' | null | undefined) ?? 'PLN',
  sessionType: normalizeTutorSessionType(target.sessionType) ?? 'AT_TEACHER',
});

/**
 * Renders the resolved target collection for a pass offer or wallet instance.
 * Preserves `targetIds` order; missing lookup rows fall back to an id label.
 */
const PassTargetCollection: React.FC<PassTargetCollectionProps> = ({
  targetType,
  targetIds,
  targets,
  audience = 'user',
  onRoomClick,
  onLessonClick,
}) => {
  const { t } = useTranslation();
  const byId = new Map(targets.map((target) => [target.id, target]));

  if (targetIds.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('passes.targetsMissing', 'Attached lesson or room is no longer available.')}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {targetIds.map((id) => {
        const target = byId.get(id);
        if (!target) {
          return (
            <Typography key={id} variant="body2" color="text.secondary">
              {id}
            </Typography>
          );
        }

        if (targetType === 'ROOM' || target.type === 'ROOM') {
          if (!target.name?.trim() && !target.city?.trim()) {
            return (
              <Typography key={id} variant="body2" color="text.secondary">
                {id}
              </Typography>
            );
          }
          return (
            <PassSpaceTargetCard
              key={id}
              target={target}
              onClick={onRoomClick ? () => onRoomClick(target) : undefined}
            />
          );
        }

        const lessonOption = toLessonOptionCard(target);
        const eligibleTutors = target.eligibleTutors ?? [];
        return (
          <TutorLessonOptionCard
            key={id}
            option={lessonOption}
            hidePrice={audience !== 'host'}
            onClick={onLessonClick ? () => onLessonClick(target) : undefined}
          >
            {eligibleTutors.length > 0 ? (
              <PassEligibleTutorsRow
                tutors={eligibleTutors}
                audience={audience === 'host' ? 'host' : 'user'}
                stopPropagation
              />
            ) : null}
          </TutorLessonOptionCard>
        );
      })}
    </Stack>
  );
};

export default PassTargetCollection;
